/**
 * Language and RTL/LTR management composable
 * Handles internationalization with proper RTL/LTR support
 * This is the single source of truth for language management
 */
export const useLanguage = () => {
  const { locale, locales, setLocale } = useI18n()

  // Available languages
  const languages = computed(() => locales.value)

  // Current language
  const currentLanguage = computed(() => {
    return locales.value.find(l => l.code === locale.value) || locales.value[0]
  })

  // Is RTL active
  const isRTL = computed(() => currentLanguage.value?.dir === 'rtl')

  // Load language from localStorage
  const loadLanguage = () => {
    if (process.client) {
      try {
        const savedLang = localStorage.getItem('userLanguage')
        if (savedLang && (savedLang === 'en' || savedLang === 'ar')) {
          return savedLang
        }
      } catch (error) {
        console.warn('Failed to load language from localStorage:', error)
      }
    }
    return 'en' // Default language
  }

  // Save language to localStorage
  const saveLanguage = (langCode: string) => {
    if (process.client) {
      try {
        localStorage.setItem('userLanguage', langCode)
        // Also update userSettings if it exists to keep them in sync
        const savedSettings = localStorage.getItem('userSettings')
        if (savedSettings) {
          const settings = JSON.parse(savedSettings)
          settings.language = langCode
          localStorage.setItem('userSettings', JSON.stringify(settings))
        }
      } catch (error) {
        console.error('Failed to save language to localStorage:', error)
      }
    }
  }

  // Switch language with persistence
  const switchLanguage = (langCode: string) => {
    if (langCode && (langCode === 'en' || langCode === 'ar')) {
      setLocale(langCode)
      saveLanguage(langCode)
      updateDirection()
    }
  }

  // Update document direction
  const updateDirection = () => {
    if (process.client) {
      const html = document.documentElement
      const body = document.body
      const currentLang = currentLanguage.value

      if (currentLang) {
        html.setAttribute('dir', currentLang.dir)
        html.setAttribute('lang', currentLang.code || 'en')
        // Update body classes for RTL/LTR styling
        body.classList.toggle('rtl', currentLang.dir === 'rtl')
        body.classList.toggle('ltr', currentLang.dir === 'ltr')
      }
    }
  }

  // Toggle between English and Arabic
  const toggleLanguage = () => {
    const newLang = locale.value === 'en' ? 'ar' : 'en'
    switchLanguage(newLang)
  }

  // Initialize language and direction on mount
  const initializeLanguage = () => {
    if (process.client) {
      const savedLang = loadLanguage()
      if (savedLang !== locale.value) {
        setLocale(savedLang)
      }
      updateDirection()
    }
  }

  return {
    locale,
    languages,
    currentLanguage,
    isRTL,
    switchLanguage,
    toggleLanguage,
    updateDirection,
    initializeLanguage,
    loadLanguage,
    saveLanguage
  }
}
